import moment from 'moment-timezone';

// ---------------------------------------------------------------------------
// Date format used by the Calendar public API: 'YYYY-MM-DD'
// ---------------------------------------------------------------------------

const DATE_FORMAT = 'YYYY-MM-DD';

// ---------------------------------------------------------------------------
// Shortcut definitions (hardcoded, range-only)
// ---------------------------------------------------------------------------

// Labels resolved lazily so i18next is guaranteed to be initialised.
export const SHORTCUT_OPTIONS = [
    { value: '1d', labelKey: 'periods.1d_short' },
    { value: '1w', labelKey: 'periods.1w_short' },
    { value: '1m', labelKey: 'periods.1m_short' },
    { value: '1y', labelKey: 'periods.1y_short' },
    { value: 'wtd', labelKey: 'periods.wtd_short' },
    { value: 'mtd', labelKey: 'periods.mtd_short' },
    { value: 'ytd', labelKey: 'periods.ytd_short' },
    { value: 'all', labelKey: 'periods.all_short' }
];

// ---------------------------------------------------------------------------
// Internal date helpers (Date objects used only inside the calendar grid)
// ---------------------------------------------------------------------------

export const createCalendarDate = (year, month, date) => new Date(year, month, date, 12, 0, 0, 0);

export const isSameCalendarDay = (left, right) => {
    if (!left || !right) {
        return false;
    }

    return left.getFullYear() === right.getFullYear()
        && left.getMonth() === right.getMonth()
        && left.getDate() === right.getDate();
};

export const addMonthsDate = (value, amount) => createCalendarDate(value.getFullYear(), value.getMonth() + amount, 1);

export const startOfMonthDate = value => createCalendarDate(value.getFullYear(), value.getMonth(), 1);

export const isMonthAfter = (left, right) => (
    left.getFullYear() > right.getFullYear()
        || (left.getFullYear() === right.getFullYear() && left.getMonth() > right.getMonth())
);

export const clampVisibleMonth = (value, minVisibleMonth, maxVisibleMonth) => {
    if (isMonthAfter(minVisibleMonth, value)) {
        return minVisibleMonth;
    }

    if (isMonthAfter(value, maxVisibleMonth)) {
        return maxVisibleMonth;
    }

    return startOfMonthDate(value);
};

export const hasAnySixWeekMonth = (minDateStr, maxDateStr) => {
    const min = toDateObject(minDateStr);
    const max = toDateObject(maxDateStr);

    if (!min || !max) {
        return true;
    }

    let cursor = startOfMonthDate(min);
    const end = startOfMonthDate(max);

    while (!isMonthAfter(cursor, end)) {
        const firstDayOfWeek = new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay();
        const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
        const rows = Math.ceil((firstDayOfWeek + daysInMonth) / 7);

        if (rows === 6) {
            return true;
        }

        cursor = addMonthsDate(cursor, 1);
    }

    return false;
};

// ---------------------------------------------------------------------------
// String ↔ Date conversions
// ---------------------------------------------------------------------------

export const toDateObject = value => {
    if (!value) {
        return undefined;
    }

    const parsed = moment(value, DATE_FORMAT, true);

    if (!parsed.isValid()) {
        return undefined;
    }

    return createCalendarDate(parsed.year(), parsed.month(), parsed.date());
};

export const formatCalendarDateValue = value => {
    if (!value) {
        return '';
    }

    return [
        value.getFullYear(),
        String(value.getMonth() + 1).padStart(2, '0'),
        String(value.getDate()).padStart(2, '0')
    ].join('-');
};

export const formatCalendarMonthLabel = (value, timezone) => (
    moment.tz({
        day: 1,
        month: value.getMonth(),
        year: value.getFullYear()
    }, timezone).format('MMMM YYYY')
);

export const formatTriggerLabel = (start, finish) => `${start} - ${finish}`;

// ---------------------------------------------------------------------------
// Shortcut range computation
// ---------------------------------------------------------------------------

export const getShortcutRange = (alias, timezone, { minDate, maxDate } = {}) => {
    if (alias === 'all') {
        return {
            start: minDate,
            finish: maxDate
        };
    }

    const now = moment.tz(new Date(), timezone);
    const todayStart = now.clone().startOf('day');
    const todayEnd = now.clone().endOf('day');

    let fromMoment;
    let toMoment;

    switch (alias) {
        case '1d':
            fromMoment = todayStart;
            toMoment = todayEnd;
            break;
        case '1w':
            fromMoment = todayStart.clone().subtract(7, 'days');
            toMoment = todayEnd;
            break;
        case 'wtd':
            fromMoment = now.clone().startOf('isoWeek');
            toMoment = todayEnd;
            break;
        case 'mtd':
            fromMoment = now.clone().startOf('month');
            toMoment = todayEnd;
            break;
        case '1m':
            fromMoment = todayStart.clone().subtract(1, 'month');
            toMoment = todayEnd;
            break;
        case '1y':
            fromMoment = todayStart.clone().subtract(1, 'year');
            toMoment = todayEnd;
            break;
        case 'ytd':
            fromMoment = now.clone().startOf('year');
            toMoment = todayEnd;
            break;
        default:
            fromMoment = todayStart;
            toMoment = todayEnd;
            break;
    }

    return {
        start: fromMoment.format(DATE_FORMAT),
        finish: toMoment.format(DATE_FORMAT)
    };
};

// ---------------------------------------------------------------------------
// Active shortcut detection
// ---------------------------------------------------------------------------

export const detectActiveShortcut = ({ start, finish, minDate, maxDate, timezone }) => {
    if (start === minDate && finish === maxDate) {
        return 'all';
    }

    const aliases = SHORTCUT_OPTIONS.map(option => option.value);

    const rollingAlias = aliases.find(alias => {
        const range = getShortcutRange(alias, timezone, { minDate, maxDate });
        return range.start === start && range.finish === finish;
    });

    if (rollingAlias) {
        return rollingAlias;
    }

    return aliases.find(alias => isCalendarBucketAliasMatch({ alias, start, timezone, finish })) || null;
};

// ---------------------------------------------------------------------------
// Shift logic
// ---------------------------------------------------------------------------

export const canShiftInDirection = ({ direction, start, finish, minDate, maxDate, timezone }) => {
    const activeAlias = detectActiveShortcut({ start, finish, minDate, maxDate, timezone });

    if (activeAlias === 'all') {
        return false;
    }

    if (direction < 0) {
        const fromMoment = moment(start, DATE_FORMAT, true);
        const minMoment = moment(minDate, DATE_FORMAT, true);
        return fromMoment.isAfter(minMoment, 'day');
    }

    const shifted = shiftRange({ direction, start, finish, minDate, maxDate, timezone });
    return shifted.start !== start || shifted.finish !== finish;
};

export const shiftRange = ({ direction, start, finish, minDate, maxDate, timezone }) => {
    const activeAlias = detectActiveShortcut({ start, finish, minDate, maxDate, timezone });

    if (activeAlias === 'all') {
        return { start, finish };
    }

    const fromMoment = moment.tz(start, DATE_FORMAT, timezone).startOf('day');
    const toMoment = moment.tz(finish, DATE_FORMAT, timezone).endOf('day');
    const currentYear = moment.tz(new Date(), timezone).year();
    const todayEnd = moment.tz(new Date(), timezone).endOf('day');

    if (activeAlias === 'wtd') {
        if (isCurrentWtdRange({ start, finish, timezone })) {
            if (direction < 0) {
                return buildCalendarWeekRange(fromMoment.clone().subtract(1, 'week'));
            }

            return { start, finish };
        }

        if (isFullCalendarWeekRange({ start, finish, timezone })) {
            const nextWeek = fromMoment.clone().startOf('isoWeek').add(direction, 'week');

            if (direction > 0 && nextWeek.isSame(todayEnd, 'isoWeek')) {
                return getShortcutRange('wtd', timezone);
            }

            return buildCalendarWeekRange(nextWeek);
        }
    }

    if (activeAlias === 'mtd') {
        if (isCurrentMtdRange({ start, finish, timezone })) {
            if (direction < 0) {
                return buildCalendarMonthRange(fromMoment.clone().subtract(1, 'month'));
            }

            return { start, finish };
        }

        if (isFullCalendarMonthRange({ start, finish, timezone })) {
            const nextMonth = fromMoment.clone().startOf('month').add(direction, 'month');

            if (direction > 0 && nextMonth.isSame(todayEnd, 'month')) {
                return getShortcutRange('mtd', timezone);
            }

            return buildCalendarMonthRange(nextMonth);
        }
    }

    if (activeAlias === 'ytd') {
        if (isCurrentYtdRange({ start, finish, timezone })) {
            if (direction < 0) {
                return buildCalendarYearRange(currentYear - 1, timezone);
            }

            return { start, finish };
        }

        if (isFullCalendarYearRange({ start, finish, timezone })) {
            const visibleYear = fromMoment.year();
            const nextYear = visibleYear + direction;

            if (direction > 0 && nextYear === currentYear) {
                return getShortcutRange('ytd', timezone);
            }

            return buildCalendarYearRange(nextYear, timezone);
        }
    }

    if (isCalendarBucketAliasMatch({ alias: activeAlias, start, timezone, finish })) {
        const { amount, boundaryUnit, unit } = getCalendarBucketConfig(activeAlias);
        const nextFrom = fromMoment.clone().startOf(boundaryUnit).add(direction * amount, unit);
        const nextTo = nextFrom.clone().add(amount, unit).subtract(1, 'day').endOf('day');

        if (direction > 0 && nextTo.isAfter(todayEnd)) {
            return {
                start: nextFrom.format(DATE_FORMAT),
                finish: todayEnd.format(DATE_FORMAT)
            };
        }

        return {
            start: nextFrom.format(DATE_FORMAT),
            finish: nextTo.format(DATE_FORMAT)
        };
    }

    const inclusiveDays = toMoment.clone().startOf('day').diff(fromMoment.clone().startOf('day'), 'days') + 1;
    const nextFrom = fromMoment.clone().add(direction * inclusiveDays, 'days');
    const nextTo = toMoment.clone().add(direction * inclusiveDays, 'days');

    if (direction > 0 && nextTo.isAfter(todayEnd)) {
        const clampedTo = todayEnd.clone();
        const clampedFrom = clampedTo.clone().subtract(inclusiveDays - 1, 'days').startOf('day');

        return {
            start: clampedFrom.format(DATE_FORMAT),
            finish: clampedTo.format(DATE_FORMAT)
        };
    }

    return {
        start: nextFrom.format(DATE_FORMAT),
        finish: nextTo.format(DATE_FORMAT)
    };
};

// ---------------------------------------------------------------------------
// Calendar-bucket helpers (private)
// ---------------------------------------------------------------------------

const getCalendarBucketConfig = alias => {
    switch (alias) {
        case 'wtd':
            return { amount: 1, boundaryUnit: 'isoWeek', unit: 'week' };
        case '1m':
            return { amount: 1, boundaryUnit: 'month', unit: 'month' };
        case '6m':
            return { amount: 6, boundaryUnit: 'month', unit: 'month' };
        case '1y':
            return { amount: 1, boundaryUnit: 'year', unit: 'year' };
        default:
            return null;
    }
};

const isCalendarBucketAliasMatch = ({ alias, start, timezone, finish }) => {
    const config = getCalendarBucketConfig(alias);

    if (!config) {
        return false;
    }

    const fromMoment = moment.tz(start, DATE_FORMAT, timezone).startOf('day');
    const toMoment = moment.tz(finish, DATE_FORMAT, timezone).endOf('day');
    const expectedFrom = fromMoment.clone().startOf(config.boundaryUnit);
    const expectedTo = expectedFrom.clone().add(config.amount, config.unit).subtract(1, 'day').endOf('day');

    return fromMoment.isSame(expectedFrom, 'day') && toMoment.isSame(expectedTo, 'day');
};

const buildCalendarYearRange = (year, timezone) => ({
    start: moment.tz({ day: 1, month: 0, year }, timezone).startOf('day').format(DATE_FORMAT),
    finish: moment.tz({ day: 31, month: 11, year }, timezone).endOf('day').format(DATE_FORMAT)
});

const buildCalendarMonthRange = monthMoment => ({
    start: monthMoment.clone().startOf('month').startOf('day').format(DATE_FORMAT),
    finish: monthMoment.clone().endOf('month').endOf('day').format(DATE_FORMAT)
});

const buildCalendarWeekRange = weekMoment => ({
    start: weekMoment.clone().startOf('isoWeek').startOf('day').format(DATE_FORMAT),
    finish: weekMoment.clone().endOf('isoWeek').endOf('day').format(DATE_FORMAT)
});

const isCurrentYtdRange = ({ start, finish, timezone }) => {
    const range = getShortcutRange('ytd', timezone);
    return range.start === start && range.finish === finish;
};

const isCurrentWtdRange = ({ start, finish, timezone }) => {
    const range = getShortcutRange('wtd', timezone);
    return range.start === start && range.finish === finish;
};

const isCurrentMtdRange = ({ start, finish, timezone }) => {
    const range = getShortcutRange('mtd', timezone);
    return range.start === start && range.finish === finish;
};

const isFullCalendarWeekRange = ({ start, finish, timezone }) => {
    const fromMoment = moment.tz(start, DATE_FORMAT, timezone).startOf('day');
    const toMoment = moment.tz(finish, DATE_FORMAT, timezone).endOf('day');
    const expectedFrom = fromMoment.clone().startOf('isoWeek');
    const expectedTo = fromMoment.clone().endOf('isoWeek');

    return fromMoment.isSame(expectedFrom, 'day') && toMoment.isSame(expectedTo, 'day');
};

const isFullCalendarMonthRange = ({ start, finish, timezone }) => {
    const fromMoment = moment.tz(start, DATE_FORMAT, timezone).startOf('day');
    const toMoment = moment.tz(finish, DATE_FORMAT, timezone).endOf('day');
    const expectedTo = fromMoment.clone().endOf('month');

    return fromMoment.isSame(fromMoment.clone().startOf('month'), 'day')
        && toMoment.isSame(expectedTo, 'day')
        && fromMoment.isSame(toMoment, 'month');
};

const isFullCalendarYearRange = ({ start, finish, timezone }) => {
    const fromMoment = moment.tz(start, DATE_FORMAT, timezone).startOf('day');
    const toMoment = moment.tz(finish, DATE_FORMAT, timezone).endOf('day');
    const expectedTo = fromMoment.clone().endOf('year');

    return fromMoment.isSame(fromMoment.clone().startOf('year'), 'day')
        && toMoment.isSame(expectedTo, 'day')
        && fromMoment.year() === toMoment.year();
};
